/**
 * Filtros de **Operadores** y **Clientes** compartidos por los side panels de Contactos,
 * Presupuestos, Pedidos y el Panel de control — Fase 5 de `filtros-operadores-clientes`
 * (xcons-docs#306).
 *
 * La API (`GET /contacts?filter.role=operator|customer`, xcon-api!955) devuelve **una ficha por
 * persona y store**, con `afiliaciones[]` calculadas para el store de esa ficha. Lo que el operador
 * selecciona en el componente no es una persona sino una **afiliación** —una persona EN un store o
 * seller—, y eso es lo que los listados filtran (`filter.operators`, xcon-api!956). Por eso cada
 * badge lleva su propio `contactId`: la participación de un operador en un presupuesto se registra
 * con su ficha del store del presupuesto.
 */

export type AffiliationType = "store" | "seller";

/** Una fila de `GET /contacts?filter.role=…`, con lo que estos componentes leen de ella. */
export interface ContactRoleRow {
  contactId: number;
  userId: number;
  storeCode?: string;
  email: string | null;
  telefono: string | null;
  afiliaciones?: Array<{
    tipo: AffiliationType;
    codigo: string;
    nombre: string | null;
  }>;
  user?: { firstname?: string | null; lastname?: string | null } | null;
  firstname?: string | null;
  lastname?: string | null;
}

export interface OperatorBadge {
  /** La ficha del operador en el store al que pertenece esta afiliación. */
  contactId: number;
  /** La persona. El dashboard filtra métricas por persona, no por afiliación. */
  userId: number;
  type: AffiliationType;
  identifier: string;
  /** Nombre a mostrar del store o seller; cae al código si la API no lo trae. */
  label: string;
}

/** Una tarjeta del componente de Operadores: la persona, con todas sus afiliaciones visibles. */
export interface OperatorCard {
  userId: number;
  name: string;
  email: string | null;
  telefono: string | null;
  badges: OperatorBadge[];
}

/** Lo que el componente de Operadores devuelve por cada badge tildado. */
export interface OperatorAffiliationSelection extends OperatorBadge {
  personName: string;
}

export interface CustomerCard {
  contactId: number;
  name: string;
  email: string | null;
  telefono: string | null;
}

export interface CustomerSelection {
  contactId: number;
  name: string;
}

/** El nombre efectivo de la ficha: el del store si existe, si no el de la identidad. */
export function personName(row: ContactRoleRow): string {
  const first = row.firstname ?? row.user?.firstname ?? "";
  const last = row.lastname ?? row.user?.lastname ?? "";
  return `${first} ${last}`.trim() || `#${row.contactId}`;
}

/** La clave estable de una afiliación, para comparar selecciones. */
export function affiliationKey(a: OperatorBadge): string {
  return `${a.contactId}:${a.type}:${a.identifier}`;
}

/**
 * Filas → tarjetas, **agrupando por persona** (`userId`). Con varios stores elegidos la misma
 * persona viene una vez por store; la tarjeta es una sola y cada badge recuerda de qué ficha salió.
 * Los badges se deduplican por tipo + código.
 */
export function mergeOperatorCards(rows: ContactRoleRow[]): OperatorCard[] {
  const porPersona = new Map<number, OperatorCard>();
  for (const row of rows) {
    const card = porPersona.get(row.userId) ?? {
      userId: row.userId,
      name: personName(row),
      email: null,
      telefono: null,
      badges: [],
    };
    card.email = card.email ?? row.email ?? null;
    card.telefono = card.telefono ?? row.telefono ?? null;
    for (const a of row.afiliaciones ?? []) {
      const badge: OperatorBadge = {
        contactId: row.contactId,
        userId: row.userId,
        type: a.tipo,
        identifier: a.codigo,
        label: a.nombre?.trim() || a.codigo,
      };
      if (
        !card.badges.some(
          (b) => b.type === badge.type && b.identifier === badge.identifier,
        )
      ) {
        card.badges.push(badge);
      }
    }
    porPersona.set(row.userId, card);
  }
  // Sin afiliaciones no hay qué tildar: la fila no debería venir con `role=operator`, pero si viene
  // no se muestra una tarjeta vacía.
  return [...porPersona.values()].filter((c) => c.badges.length > 0);
}

export function toCustomerCards(rows: ContactRoleRow[]): CustomerCard[] {
  return rows.map((row) => ({
    contactId: row.contactId,
    name: personName(row),
    email: row.email ?? null,
    telefono: row.telefono ?? null,
  }));
}

/** El valor de `filter.operators` para `GET /quotes` y `GET /orders` (xcon-api!956). */
export function toOperatorsFilterValue(
  sel: OperatorAffiliationSelection[],
): string | undefined {
  if (!sel.length) return undefined;
  return sel.map((s) => `${s.contactId}:${s.type}:${s.identifier}`).join(",");
}

/** Las personas detrás de los badges tildados, sin repetir: lo que `GET /dashboard/metrics?operators=` recibe. */
export function toOperatorUserIds(sel: OperatorAffiliationSelection[]): number[] {
  return [...new Set(sel.map((s) => s.userId).filter((n) => Number.isFinite(n) && n > 0))];
}

/** El valor de `filter.customers` para `GET /quotes` y `GET /orders` (xcon-api!957). */
export function toCustomersFilterValue(
  sel: CustomerSelection[],
): string | undefined {
  if (!sel.length) return undefined;
  return [...new Set(sel.map((s) => String(s.contactId)))].join(",");
}

/** Inverso de `toOperatorsFilterValue`, para rehidratar desde la URL; los nombres se pierden. */
export function parseOperatorsFilterValue(
  raw: unknown,
): OperatorAffiliationSelection[] {
  return String(raw ?? "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)
    .map((item) => {
      const [cid, type, identifier] = item.split(":");
      if (
        !/^\d+$/.test(cid) ||
        (type !== "store" && type !== "seller") ||
        !identifier
      )
        return null;
      return {
        contactId: Number(cid),
        userId: 0,
        type,
        identifier,
        label: identifier,
        personName: `#${cid}`,
      } as OperatorAffiliationSelection;
    })
    .filter((x): x is OperatorAffiliationSelection => x !== null);
}

/** Inverso de `toCustomersFilterValue`, para rehidratar desde la URL; los nombres se pierden. */
export function parseCustomersFilterValue(raw: unknown): CustomerSelection[] {
  return [
    ...new Set(
      String(raw ?? "")
        .split(",")
        .map((x) => x.trim())
        .filter((x) => /^\d+$/.test(x)),
    ),
  ].map((id) => ({ contactId: Number(id), name: `#${id}` }));
}
